import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import StatsSummaryCards from '../components/StatsSummaryCards';
import { toast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import ImportDropdown from '../components/SprintImport/ImportDropdown';
import FileImportModal from '../components/SprintImport/FileImportModal';
import PasteImportModal from '../components/SprintImport/PasteImportModal';
import BulkEditModal from '../components/SprintImport/BulkEditModal';
import { generateTemplate } from '../utils/sprintTemplate';
import ImportHistory from '../components/SprintImport/ImportHistory';
import type { DetectionResult, SprintInfo } from '../utils/sprintImport';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface SprintMetrics {
  costsPlanned: number;
  costsActual: number;
  revenuePlanned: number;
  revenueActual: number;
  storyPointsPlanned: number;
  storyPointsTarget: number;
  storyPointsAchieved: number;
  manDaysPlanned: number;
  manDaysActual: number;
}

interface Sprint {
  sprintId: string;
  piId: string;
  piNumber: number;
  sprintNumber: number;
  sprintInPi: number;
  name: string;
  startDate: string;
  endDate: string;
  metrics?: SprintMetrics;
}

interface Team {
  teamId: string;
  projectId: string;
  name: string;
  description?: string;
  members: TeamMember[];
  userAccessLevel: 'viewer' | 'editor' | 'admin';
}

interface TeamMember {
  userId: string;
  role: string;
  accessLevel: string;
  name?: string;
  email?: string;
  isGlobalAdmin?: boolean;
  globalAdminLabel?: string;
  isProjectMember?: boolean;
  projectMemberLabel?: string;
}

interface User {
  userId: string;
  name: string;
  email: string;
  role?: string;
}

interface ProductIncrement {
  piId: string;
  year: number;
  piNumber: number;
  name: string;
  isSpecial: boolean;
}

const currentYear = new Date().getFullYear();

// Helper to determine sprint status based on dates
function getSprintStatus(sprint: Sprint): 'past' | 'active' | 'future' {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const startDate = new Date(sprint.startDate);
  const endDate = new Date(sprint.endDate);

  if (today > endDate) return 'past';
  if (today >= startDate && today <= endDate) return 'active';
  return 'future';
}

const TEAM_ROLES = [
  { value: 'po', label: 'Product Owner' },
  { value: 'lead_dev', label: 'Lead Developer' },
  { value: 'developer', label: 'Developer' },
  { value: 'qa', label: 'QA Engineer' },
  { value: 'tester', label: 'Tester' },
  { value: 'devops', label: 'DevOps' },
  { value: 'other', label: 'Other' },
];

const ACCESS_LEVELS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Admin' },
];

export default function TeamDetail() {
  const { teamId } = useParams<{ teamId: string }>();
  const api = useApi();
  const { user: currentUser } = useAuth();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [editingSprint, setEditingSprint] = useState<Sprint | null>(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [editTeam, setEditTeam] = useState({ name: '', description: '' });
  const [showFileImport, setShowFileImport] = useState(false);
  const [showPasteImport, setShowPasteImport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditImportData, setBulkEditImportData] = useState<DetectionResult | null>(null);

  const { data: team, isLoading: teamLoading } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => api.get<Team>(`/api/teams/${teamId}`),
  });

  const { data: sprintsData, isLoading: sprintsLoading } = useQuery({
    queryKey: ['team-sprints', teamId, selectedYear],
    queryFn: () =>
      api.get<{ items: Sprint[] }>(`/api/teams/${teamId}/sprints?year=${selectedYear}`),
  });

  const { data: scheduleData } = useQuery({
    queryKey: ['project-schedule', team?.projectId, selectedYear],
    queryFn: () =>
      api.get<{ projectType: string; items: (ProductIncrement & { sprints?: Sprint[] })[] }>(`/api/projects/${team!.projectId}/years/${selectedYear}/schedule`),
    enabled: !!team?.projectId,
  });

  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => api.get<{ items: TeamMember[] }>(`/api/teams/${teamId}/members`),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ items: User[] }>('/api/users'),
    enabled: showAddMember,
  });

  const addMemberMutation = useMutation({
    mutationFn: (data: { userId: string; role: string; accessLevel: string }) =>
      api.post(`/api/teams/${teamId}/members`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      setShowAddMember(false);
      toast.success('Member added successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add member');
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: { role?: string; accessLevel?: string } }) =>
      api.put(`/api/teams/${teamId}/members/${userId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      setEditingMember(null);
      toast.success('Member updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update member');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => api.delete(`/api/teams/${teamId}/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      toast.success('Member removed successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove member');
    },
  });

  const handleRemoveMember = async (member: TeamMember) => {
    const confirmed = await confirm({
      title: 'Remove Member',
      message: `Are you sure you want to remove "${member.name || member.userId}" from this team?`,
      confirmText: 'Remove',
      variant: 'danger',
    });

    if (confirmed) {
      removeMemberMutation.mutate(member.userId);
    }
  };

  const updateTeamMutation = useMutation({
    mutationFn: (data: { name: string; description: string }) =>
      api.put(`/api/teams/${teamId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setShowEditTeam(false);
      toast.success('Team updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update team');
    },
  });

  const handleEditTeam = () => {
    if (team) {
      setEditTeam({ name: team.name, description: team.description || '' });
      setShowEditTeam(true);
    }
  };

  const updateSprintMutation = useMutation({
    mutationFn: ({ sprintId, data }: { sprintId: string; data: { teamId: string; metrics: SprintMetrics } }) =>
      api.put(`/api/sprints/${sprintId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-sprints', teamId, selectedYear] });
      setEditingSprint(null);
      toast.success('Sprint metrics updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update sprint metrics');
    },
  });

  const deleteMetricsMutation = useMutation({
    mutationFn: (sprintId: string) =>
      api.delete(`/api/sprints/${sprintId}/metrics?teamId=${teamId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-sprints', teamId, selectedYear] });
      toast.success('Sprint metrics deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete sprint metrics');
    },
  });

  const bulkSaveMutation = useMutation({
    mutationFn: (items: Array<{ sprintId: string; metrics: Partial<SprintMetrics> }>) =>
      api.put(`/api/teams/${teamId}/bulk-metrics`, { items }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['team-sprints', teamId, selectedYear] });
      queryClient.invalidateQueries({ queryKey: ['metrics-history', teamId] });
      setShowBulkEdit(false);
      setShowFileImport(false);
      setShowPasteImport(false);
      setBulkEditImportData(null);
      toast.success(`${variables.length} sprint metrics saved successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save metrics');
    },
  });

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['metrics-history', teamId],
    queryFn: () => api.get<{ items: Array<{ auditId: string; timestamp: string; userName: string; action: string; itemCount: number; year: number }> }>(`/api/teams/${teamId}/metrics-history`),
  });

  const revertMutation = useMutation({
    mutationFn: (auditId: string) =>
      api.post(`/api/teams/${teamId}/metrics-history/${auditId}/revert`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-sprints', teamId, selectedYear] });
      queryClient.invalidateQueries({ queryKey: ['metrics-history', teamId] });
      toast.success('Import reverted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to revert import');
    },
  });

  const handleImportDirect = (result: DetectionResult) => {
    const items: Array<{ sprintId: string; metrics: Partial<SprintMetrics> }> = [];
    for (const row of result.rows) {
      if (!row.sprintMatch.sprint) continue;
      const metrics: Partial<SprintMetrics> = {};
      let hasValues = false;
      for (const [key, val] of Object.entries(row.values)) {
        if (val !== null && val !== undefined) {
          (metrics as Record<string, number>)[key] = val;
          hasValues = true;
        }
      }
      if (hasValues) {
        items.push({ sprintId: row.sprintMatch.sprint.sprintId, metrics });
      }
    }
    if (items.length > 0) {
      bulkSaveMutation.mutate(items);
    }
  };

  const handleBulkEditFromImport = (result: DetectionResult) => {
    setBulkEditImportData(result);
    setShowFileImport(false);
    setShowPasteImport(false);
    setShowBulkEdit(true);
  };

  const handleDeleteMetrics = async (sprint: Sprint) => {
    const confirmed = await confirm({
      title: 'Delete Sprint Metrics',
      message: `Are you sure you want to delete metrics for "${sprint.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (confirmed) {
      deleteMetricsMutation.mutate(sprint.sprintId);
    }
  };

  if (teamLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">Team not found</p>
        <Link to="/teams" className="btn-primary mt-4 inline-block">
          Back to Teams
        </Link>
      </div>
    );
  }

  const sprints = sprintsData?.items || [];
  const hasPIs = scheduleData?.projectType === 'pi-based';
  const pis = hasPIs ? (scheduleData?.items || []) : [];
  const members = membersData?.items || [];
  const availableUsers = usersData?.items || [];

  const sprintInfos: SprintInfo[] = sprints.map((s) => ({
    sprintId: s.sprintId,
    name: s.name,
    sprintNumber: s.sprintNumber,
    startDate: s.startDate,
    endDate: s.endDate,
  }));

  const existingMetricsCount = sprints.filter((s) => s.metrics).length;

  const handleDownloadTemplate = () => {
    if (!team || sprintInfos.length === 0) return;
    const blob = generateTemplate(sprintInfos, team.name);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${team.name.replace(/[^a-zA-Z0-9]/g, '_')}_metrics_template.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Determine user's permission level
  const canEdit = team?.userAccessLevel === 'admin' || team?.userAccessLevel === 'editor';
  const canAdmin = team?.userAccessLevel === 'admin';

  // Filter out users who are already members
  const existingMemberIds = new Set(members.map((m) => m.userId));
  const usersToAdd = availableUsers.filter(
    (u) => !existingMemberIds.has(u.userId) && u.role !== 'superadmin' && u.role !== 'admin'
  );

  // Group sprints by PI (only for PI-based projects)
  const sprintsByPi = new Map<string, { pi: ProductIncrement; sprints: Sprint[] }>();
  if (hasPIs) {
    for (const pi of pis) {
      sprintsByPi.set(pi.piId, { pi, sprints: [] });
    }
    for (const sprint of sprints) {
      if (sprint.piId) {
        const piGroup = sprintsByPi.get(sprint.piId);
        if (piGroup) {
          piGroup.sprints.push(sprint);
        }
      }
    }
  }

  // Convert to array and sort by PI number (show all PIs, even without sprints)
  const groupedSprints = Array.from(sprintsByPi.values())
    .sort((a, b) => a.pi.piNumber - b.pi.piNumber);

  // Sprints with metrics for chart (exclude future sprints)
  const now = new Date();
  const allSprints = sprints
    .filter((s) => s.metrics && new Date(s.startDate) <= now)
    .sort((a, b) => a.sprintNumber - b.sprintNumber);

  // Compute year statistics from sprint metrics
  const totalCosts = allSprints.reduce((sum, s) => sum + (s.metrics?.costsActual || 0), 0);
  const totalRevenue = allSprints.reduce((sum, s) => sum + (s.metrics?.revenueActual || 0), 0);
  const costOverRevenue = totalRevenue > 0 ? totalCosts / totalRevenue : 0;

  // Compute per-sprint data with accumulated values
  let accCosts = 0;
  let accRevenue = 0;
  const sprintDataPoints = allSprints.map((s) => {
    const costs = s.metrics?.costsActual || 0;
    const revenue = s.metrics?.revenueActual || 0;
    const cr = revenue > 0 ? costs / revenue : 0;
    accCosts += costs;
    accRevenue += revenue;
    return {
      name: s.name,
      costs,
      revenue,
      costOverRevenue: cr,
      accumulatedCostOverRevenue: accRevenue > 0 ? accCosts / accRevenue : 0,
    };
  });

  const isDark = theme === 'dark';
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { color: isDark ? '#9ca3af' : '#4b5563' },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
        ticks: { color: isDark ? '#9ca3af' : '#4b5563' },
      },
      x: {
        grid: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
        ticks: { color: isDark ? '#9ca3af' : '#4b5563' },
      },
    },
  };

  // Year options
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 dark:text-gray-400">
        <Link to="/teams" className="hover:text-gray-900 dark:hover:text-white">
          Teams
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900 dark:text-white">{team.name}</span>
      </nav>

      {/* Team header */}
      <div className="card">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{team.name}</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">{team.description || 'No description'}</p>
          </div>
          {canAdmin && (
            <button onClick={handleEditTeam} className="btn-outline">
              Edit Team
            </button>
          )}
        </div>

        <div className="flex gap-6 mt-6 pt-6 border-t border-gray-200 dark:border-dark-500 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Members:</span>
            <span className="ml-2 font-medium">{members.length}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Completed Sprints:</span>
            <span className="ml-2 font-medium">{allSprints.length}</span>
          </div>
        </div>
      </div>

      {/* Year Statistics */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Year Statistics</h2>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="input py-1.5 px-3 w-24"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {sprintsLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : allSprints.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No metrics data available for {selectedYear}</p>
            <p className="text-sm mt-1">Add sprint metrics to see team statistics</p>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="mb-6">
              <StatsSummaryCards totalCosts={totalCosts} totalRevenue={totalRevenue} costOverRevenue={costOverRevenue} />
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Costs & Revenue Chart */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">Costs & Revenue per Sprint</h3>
                <div className="h-64">
                  <Line
                    data={{
                      labels: sprintDataPoints.map((s) => s.name),
                      datasets: [
                        {
                          label: 'Costs',
                          data: sprintDataPoints.map((s) => s.costs),
                          borderColor: '#E31837',
                          backgroundColor: 'rgba(227, 24, 55, 0.1)',
                          tension: 0.3,
                        },
                        {
                          label: 'Revenue',
                          data: sprintDataPoints.map((s) => s.revenue),
                          borderColor: '#2DE2BF',
                          backgroundColor: 'rgba(45, 226, 191, 0.1)',
                          tension: 0.3,
                        },
                      ],
                    }}
                    options={chartOptions}
                  />
                </div>
              </div>

              {/* C/R Ratio Chart */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-3">C/R Ratio (Sprint & Accumulated)</h3>
                <div className="h-64">
                  <Line
                    data={{
                      labels: sprintDataPoints.map((s) => s.name),
                      datasets: [
                        {
                          label: 'Sprint C/R',
                          data: sprintDataPoints.map((s) => s.costOverRevenue),
                          borderColor: '#0066FF',
                          backgroundColor: 'rgba(0, 102, 255, 0.1)',
                          tension: 0.3,
                        },
                        {
                          label: 'Accumulated C/R',
                          data: sprintDataPoints.map((s) => s.accumulatedCostOverRevenue),
                          borderColor: '#D0006F',
                          backgroundColor: 'rgba(208, 0, 111, 0.1)',
                          tension: 0.3,
                          borderDash: [5, 5],
                        },
                      ],
                    }}
                    options={chartOptions}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Team Members */}
      <div className="card">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Team Members</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              People working on this team with their roles and access levels.
            </p>
          </div>
          {canAdmin && (
            <button
              onClick={() => setShowAddMember(true)}
              className="btn-primary"
            >
              Add Member
            </button>
          )}
        </div>

        {membersLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : members.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">No members yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-500">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Name</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Access</th>
                  {canAdmin && (
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {members.map((member) => {
                  const isInherited = member.isGlobalAdmin || member.isProjectMember;
                  return (
                  <tr key={member.userId} className="border-b border-gray-100 dark:border-dark-600 hover:bg-gray-50 dark:hover:bg-dark-600">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{member.name || 'Unknown'}</span>
                        {member.userId === currentUser?.userId && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            You
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{member.email || '-'}</td>
                    <td className="py-3 px-4">
                      {member.isGlobalAdmin ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                          {member.globalAdminLabel}
                        </span>
                      ) : member.isProjectMember ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          {member.projectMemberLabel}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-turquoise/20 text-accent-turquoise">
                          {TEAM_ROLES.find((r) => r.value === member.role)?.label || member.role}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.isGlobalAdmin || member.accessLevel === 'admin'
                            ? 'bg-primary-500/20 text-primary-600 dark:text-primary-400'
                            : member.accessLevel === 'editor'
                              ? 'bg-accent-blue/20 text-blue-600 dark:text-blue-400'
                              : 'bg-gray-100 dark:bg-dark-600 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {member.isGlobalAdmin ? 'Full Access' : ACCESS_LEVELS.find((a) => a.value === member.accessLevel)?.label || member.accessLevel}
                      </span>
                    </td>
                    {canAdmin && (
                      <td className="text-right py-3 px-4">
                        {!isInherited && (
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => setEditingMember(member)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-accent-blue hover:bg-accent-blue/10 transition-colors"
                              title="Edit member"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRemoveMember(member)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                              title="Remove member"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sprints */}
      <div className="card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {hasPIs ? 'Sprints by Product Increment' : 'Sprints'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Track costs, revenue, and story points for each sprint in this team.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="input py-1.5 px-3 w-24"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {canEdit && sprints.length > 0 && (
              <ImportDropdown
                onExcelImport={() => setShowFileImport(true)}
                onPaste={() => setShowPasteImport(true)}
                onBulkEdit={() => { setBulkEditImportData(null); setShowBulkEdit(true); }}
                onDownloadTemplate={handleDownloadTemplate}
              />
            )}
          </div>
        </div>

        {sprintsLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          </div>
        ) : !hasPIs && sprints.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No sprints configured for {selectedYear}</p>
            <p className="text-sm mt-1">Schedules are managed at the project level. <Link to={`/projects/${team.projectId}`} className="text-accent-blue hover:underline">Go to project settings</Link> to initialize the schedule.</p>
          </div>
        ) : hasPIs && groupedSprints.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>No PIs configured for {selectedYear}</p>
            <p className="text-sm mt-1">Schedules are managed at the project level. <Link to={`/projects/${team.projectId}`} className="text-accent-blue hover:underline">Go to project settings</Link> to initialize the schedule.</p>
          </div>
        ) : !hasPIs ? (
          /* Flat sprint list for non-PI projects */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-500 bg-gray-50 dark:bg-dark-600">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Sprint</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Costs</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Revenue</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">C/R</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Story Points</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sprints.map((sprint) => {
                  const metrics = sprint.metrics;
                  const cr = metrics && metrics.revenueActual > 0 ? metrics.costsActual / metrics.revenueActual : 0;
                  const status = getSprintStatus(sprint);
                  const isActive = status === 'active';
                  const isFuture = status === 'future';
                  return (
                    <tr key={sprint.sprintId} className={`border-b border-gray-100 dark:border-dark-600 ${isActive ? 'bg-primary-50 hover:bg-primary-100' : isFuture ? 'bg-gray-50 dark:bg-dark-600/30 text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-600' : metrics ? 'hover:bg-gray-50 dark:hover:bg-dark-600' : 'bg-gray-50 dark:bg-dark-600/50 text-gray-500 dark:text-gray-400'}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className={isActive ? 'font-semibold text-primary-600 dark:text-primary-400' : isFuture ? 'text-gray-400' : metrics ? 'font-medium text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>{sprint.name}</span>
                          {isActive && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-primary-500 text-white">Active</span>}
                          {isFuture && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-400">Upcoming</span>}
                        </div>
                        <div className={`text-xs ${isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                          {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="text-right py-2 px-4">{metrics ? <><div className={isActive ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-white'}>{metrics.costsActual.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div><div className="text-xs text-gray-500 dark:text-gray-400">/ {metrics.costsPlanned.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div></> : <span className={isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>-</span>}</td>
                      <td className="text-right py-2 px-4">{metrics ? <><div className={isActive ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-white'}>{metrics.revenueActual.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div><div className="text-xs text-gray-500 dark:text-gray-400">/ {metrics.revenuePlanned.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</div></> : <span className={isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>-</span>}</td>
                      <td className="text-right py-2 px-4">{metrics ? <span className={`font-medium ${cr > 1 ? 'text-primary-600 dark:text-primary-400' : 'text-accent-aquamarine'}`}>{cr.toFixed(2)}</span> : <span className={isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>-</span>}</td>
                      <td className="text-right py-2 px-4">{metrics ? <><div className={isActive ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-white'}>{metrics.storyPointsAchieved}</div><div className="text-xs text-gray-500 dark:text-gray-400">/ {metrics.storyPointsPlanned} planned</div></> : <span className={isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>-</span>}</td>
                      <td className="text-right py-2 px-4">{canEdit ? <div className="flex justify-end gap-2"><button onClick={() => setEditingSprint(sprint)} className={`text-sm font-medium ${isFuture ? 'text-gray-400 hover:text-gray-400' : 'text-primary-500 hover:text-primary-600'}`}>{metrics ? 'Edit' : 'Add Metrics'}</button>{metrics && <button onClick={() => handleDeleteMetrics(sprint)} className="text-primary-400 hover:text-primary-400 text-sm font-medium">Delete</button>}</div> : <span className="text-gray-500 dark:text-gray-400 text-sm">View only</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedSprints.map(({ pi, sprints: piSprints }) => (
              <div key={pi.piId} className="border border-gray-200 dark:border-dark-500 rounded-lg overflow-hidden">
                <div
                  className={`px-4 py-3 font-medium ${
                    pi.isSpecial ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 dark:bg-dark-600 text-gray-800 dark:text-gray-200'
                  }`}
                >
                  {pi.name} {pi.isSpecial && '(Special)'}
                  <span className="text-sm font-normal ml-2 text-gray-500 dark:text-gray-400">
                    {piSprints.filter((s) => s.metrics).length} / {piSprints.length} sprints completed
                  </span>
                </div>
                {piSprints.length === 0 ? (
                  <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No sprints loaded for this PI
                  </div>
                ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-dark-500 bg-gray-50 dark:bg-dark-600">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Sprint
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Costs
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Revenue
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                          C/R
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Story Points
                        </th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 dark:text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {piSprints.map((sprint) => {
                        const metrics = sprint.metrics;
                        const cr =
                          metrics && metrics.revenueActual > 0
                            ? metrics.costsActual / metrics.revenueActual
                            : 0;
                        const status = getSprintStatus(sprint);
                        const isActive = status === 'active';
                        const isFuture = status === 'future';

                        return (
                          <tr
                            key={sprint.sprintId}
                            className={`border-b border-gray-100 dark:border-dark-600 ${
                              isActive
                                ? 'bg-primary-50 hover:bg-primary-100'
                                : isFuture
                                  ? 'bg-gray-50 dark:bg-dark-600/30 text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-600'
                                  : metrics
                                    ? 'hover:bg-gray-50 dark:hover:bg-dark-600'
                                    : 'bg-gray-50 dark:bg-dark-600/50 text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span
                                  className={
                                    isActive
                                      ? 'font-semibold text-primary-600 dark:text-primary-400'
                                      : isFuture
                                        ? 'text-gray-400'
                                        : metrics
                                          ? 'font-medium text-gray-900 dark:text-white'
                                          : 'text-gray-500 dark:text-gray-400'
                                  }
                                >
                                  {sprint.name}
                                </span>
                                {isActive && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-primary-500 text-white">
                                    Active
                                  </span>
                                )}
                                {isFuture && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-400">
                                    Upcoming
                                  </span>
                                )}
                              </div>
                              <div className={`text-xs ${isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}`}>
                                {new Date(sprint.startDate).toLocaleDateString()} -{' '}
                                {new Date(sprint.endDate).toLocaleDateString()}
                              </div>
                            </td>
                            <td className="text-right py-2 px-4">
                              {metrics ? (
                                <>
                                  <div className={isActive ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-white'}>
                                    {metrics.costsActual.toLocaleString('de-DE', {
                                      style: 'currency',
                                      currency: 'EUR',
                                    })}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    /{' '}
                                    {metrics.costsPlanned.toLocaleString('de-DE', {
                                      style: 'currency',
                                      currency: 'EUR',
                                    })}
                                  </div>
                                </>
                              ) : (
                                <span className={isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>-</span>
                              )}
                            </td>
                            <td className="text-right py-2 px-4">
                              {metrics ? (
                                <>
                                  <div className={isActive ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-white'}>
                                    {metrics.revenueActual.toLocaleString('de-DE', {
                                      style: 'currency',
                                      currency: 'EUR',
                                    })}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    /{' '}
                                    {metrics.revenuePlanned.toLocaleString('de-DE', {
                                      style: 'currency',
                                      currency: 'EUR',
                                    })}
                                  </div>
                                </>
                              ) : (
                                <span className={isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>-</span>
                              )}
                            </td>
                            <td className="text-right py-2 px-4">
                              {metrics ? (
                                <span
                                  className={`font-medium ${
                                    cr > 1 ? 'text-primary-600 dark:text-primary-400' : 'text-accent-aquamarine'
                                  }`}
                                >
                                  {cr.toFixed(2)}
                                </span>
                              ) : (
                                <span className={isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>-</span>
                              )}
                            </td>
                            <td className="text-right py-2 px-4">
                              {metrics ? (
                                <>
                                  <div className={isActive ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-white'}>
                                    {metrics.storyPointsAchieved}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    / {metrics.storyPointsPlanned} planned
                                  </div>
                                </>
                              ) : (
                                <span className={isFuture ? 'text-gray-400 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>-</span>
                              )}
                            </td>
                            <td className="text-right py-2 px-4">
                              {canEdit ? (
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => setEditingSprint(sprint)}
                                    className={`text-sm font-medium ${
                                      isFuture
                                        ? 'text-gray-400 hover:text-gray-400'
                                        : 'text-primary-500 hover:text-primary-600'
                                    }`}
                                  >
                                    {metrics ? 'Edit' : 'Add Metrics'}
                                  </button>
                                  {metrics && (
                                    <button
                                      onClick={() => handleDeleteMetrics(sprint)}
                                      className="text-primary-400 hover:text-primary-400 text-sm font-medium"
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400 text-sm">View only</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Import History */}
      {canEdit && (
        <ImportHistory
          teamId={teamId || ''}
          history={historyData?.items || []}
          isLoading={historyLoading}
          canEdit={canEdit}
          onRevert={async (auditId) => {
            const confirmed = await confirm({
              title: 'Revert Import',
              message: 'This will restore the previous metric values. A new history entry will be created so you can undo this revert if needed.',
              confirmText: 'Revert',
              variant: 'warning',
            });
            if (confirmed) {
              revertMutation.mutate(auditId);
            }
          }}
          isReverting={revertMutation.isPending}
        />
      )}

      {/* Edit team modal */}
      {showEditTeam && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <div className="card max-w-md w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Edit Team</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateTeamMutation.mutate(editTeam);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="label">Team Name</label>
                  <input
                    type="text"
                    className="input"
                    value={editTeam.name}
                    onChange={(e) => setEditTeam({ ...editTeam, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    rows={3}
                    value={editTeam.description}
                    onChange={(e) => setEditTeam({ ...editTeam, description: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowEditTeam(false)} className="btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={updateTeamMutation.isPending}>
                  {updateTeamMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit sprint modal */}
      {editingSprint && teamId && (
        <SprintEditModal
          sprint={editingSprint}
          teamId={teamId}
          onClose={() => setEditingSprint(null)}
          onSave={(data) =>
            updateSprintMutation.mutate({ sprintId: editingSprint.sprintId, data })
          }
          isSaving={updateSprintMutation.isPending}
        />
      )}

      {/* Add member modal */}
      {showAddMember && (
        <AddMemberModal
          users={usersToAdd}
          onClose={() => setShowAddMember(false)}
          onSave={(data) => addMemberMutation.mutate(data)}
          isSaving={addMemberMutation.isPending}
        />
      )}

      {/* Edit member modal */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSave={(data) =>
            updateMemberMutation.mutate({ userId: editingMember.userId, data })
          }
          isSaving={updateMemberMutation.isPending}
        />
      )}

      {/* File import modal */}
      {showFileImport && (
        <FileImportModal
          sprints={sprintInfos}
          existingMetricsCount={existingMetricsCount}
          onImport={handleImportDirect}
          onBulkEdit={handleBulkEditFromImport}
          onClose={() => setShowFileImport(false)}
        />
      )}

      {/* Paste import modal */}
      {showPasteImport && (
        <PasteImportModal
          sprints={sprintInfos}
          existingMetricsCount={existingMetricsCount}
          onImport={handleImportDirect}
          onBulkEdit={handleBulkEditFromImport}
          onClose={() => setShowPasteImport(false)}
        />
      )}

      {/* Bulk edit modal */}
      {showBulkEdit && (
        <BulkEditModal
          sprints={sprints.map((s) => ({
            sprintId: s.sprintId,
            name: s.name,
            startDate: s.startDate,
            endDate: s.endDate,
            piNumber: s.piNumber,
            metrics: s.metrics,
          }))}
          sprintInfos={sprintInfos}
          teamId={teamId || ''}
          importData={bulkEditImportData}
          onSave={(items) => bulkSaveMutation.mutate(items)}
          onClose={() => { setShowBulkEdit(false); setBulkEditImportData(null); }}
          onDownloadTemplate={handleDownloadTemplate}
          isSaving={bulkSaveMutation.isPending}
        />
      )}
    </div>
  );
}

function SprintEditModal({
  sprint,
  teamId,
  onClose,
  onSave,
  isSaving,
}: {
  sprint: Sprint;
  teamId: string;
  onClose: () => void;
  onSave: (data: { teamId: string; metrics: SprintMetrics }) => void;
  isSaving: boolean;
}) {
  const defaultMetrics: SprintMetrics = {
    costsPlanned: 0,
    costsActual: 0,
    revenuePlanned: 0,
    revenueActual: 0,
    storyPointsPlanned: 0,
    storyPointsTarget: 0,
    storyPointsAchieved: 0,
    manDaysPlanned: 0,
    manDaysActual: 0,
  };

  const [metrics, setMetrics] = useState<SprintMetrics>(sprint.metrics || defaultMetrics);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ teamId, metrics });
  };

  const updateMetric = (key: keyof SprintMetrics, value: string) => {
    setMetrics({ ...metrics, [key]: parseFloat(value) || 0 });
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 overflow-y-auto">
      <div className="card max-w-2xl w-full mx-4 my-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">
              {sprint.metrics ? 'Edit' : 'Add'} Metrics for {sprint.name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Financial Section */}
            <div className="bg-gray-100 dark:bg-dark-600 rounded-lg p-4 border border-gray-200 dark:border-dark-500">
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-accent-aquamarine" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Financial
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">Costs</p>
                  <div>
                    <label className="label">Planned</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
                      <input
                        type="number"
                        step="0.01"
                        className="input pl-7"
                        value={metrics.costsPlanned}
                        onChange={(e) => updateMetric('costsPlanned', e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label">Actual</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
                      <input
                        type="number"
                        step="0.01"
                        className="input pl-7"
                        value={metrics.costsActual}
                        onChange={(e) => updateMetric('costsActual', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-xs font-medium text-secondary-600 uppercase tracking-wide">Revenue</p>
                  <div>
                    <label className="label">Planned</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
                      <input
                        type="number"
                        step="0.01"
                        className="input pl-7"
                        value={metrics.revenuePlanned}
                        onChange={(e) => updateMetric('revenuePlanned', e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label">Actual</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">€</span>
                      <input
                        type="number"
                        step="0.01"
                        className="input pl-7"
                        value={metrics.revenueActual}
                        onChange={(e) => updateMetric('revenueActual', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Story Points Section */}
            <div className="bg-gray-100 dark:bg-dark-600 rounded-lg p-4 border border-gray-200 dark:border-dark-500">
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Story Points
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">Planned</label>
                  <input
                    type="number"
                    className="input"
                    value={metrics.storyPointsPlanned}
                    onChange={(e) => updateMetric('storyPointsPlanned', e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Target</label>
                  <input
                    type="number"
                    className="input"
                    value={metrics.storyPointsTarget}
                    onChange={(e) => updateMetric('storyPointsTarget', e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Achieved</label>
                  <input
                    type="number"
                    className="input"
                    value={metrics.storyPointsAchieved}
                    onChange={(e) => updateMetric('storyPointsAchieved', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Capacity Section */}
            <div className="bg-gray-100 dark:bg-dark-600 rounded-lg p-4 border border-gray-200 dark:border-dark-500">
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Capacity (Man Days)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Planned</label>
                  <input
                    type="number"
                    step="0.5"
                    className="input"
                    value={metrics.manDaysPlanned}
                    onChange={(e) => updateMetric('manDaysPlanned', e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Actual</label>
                  <input
                    type="number"
                    step="0.5"
                    className="input"
                    value={metrics.manDaysActual}
                    onChange={(e) => updateMetric('manDaysActual', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-dark-500">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Metrics'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddMemberModal({
  users,
  onClose,
  onSave,
  isSaving,
}: {
  users: User[];
  onClose: () => void;
  onSave: (data: { userId: string; role: string; accessLevel: string }) => void;
  isSaving: boolean;
}) {
  const [selectedUser, setSelectedUser] = useState('');
  const [role, setRole] = useState('developer');
  const [accessLevel, setAccessLevel] = useState('viewer');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    onSave({ userId: selectedUser, role, accessLevel });
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="card max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold mb-4">Add Team Member</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="label">User</label>
              <select
                className="input"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                required
              >
                <option value="">Select a user...</option>
                {users.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
              {users.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">No users available to add</p>
              )}
            </div>
            <div>
              <label className="label">Role</label>
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {TEAM_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Access Level</label>
              <select
                className="input"
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
              >
                {ACCESS_LEVELS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSaving || !selectedUser}
            >
              {isSaving ? 'Adding...' : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditMemberModal({
  member,
  onClose,
  onSave,
  isSaving,
}: {
  member: TeamMember;
  onClose: () => void;
  onSave: (data: { role?: string; accessLevel?: string }) => void;
  isSaving: boolean;
}) {
  const [role, setRole] = useState(member.role);
  const [accessLevel, setAccessLevel] = useState(member.accessLevel);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ role, accessLevel });
  };

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="card max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold mb-4">Edit Member: {member.name}</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="label">Role</label>
              <select
                className="input"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {TEAM_ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Access Level</label>
              <select
                className="input"
                value={accessLevel}
                onChange={(e) => setAccessLevel(e.target.value)}
              >
                {ACCESS_LEVELS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="btn-outline">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
